import type { SerializedDockview } from 'dockview';
import { uniq } from 'lodash-es';
import { MAIN_GROUP_ID } from '../../../components/Layout/tokens';
import { getContentComponentsInLayout } from './getContentComponentsInLayout';
import { getLargestLayoutGroup } from './getLargestGroup';
import { getLayoutGroupById } from './getLayoutGroupById';
import {
  getAllViewIdsInLayout,
  getHomeGroupByContentComponent,
  getViewsByGroupId,
  visitLayoutLeaves,
} from './layoutGridUtils';

export function resetLayoutToDefault(
  currentLayout: SerializedDockview,
  defaultLayout: SerializedDockview
): SerializedDockview {
  const newLayout = structuredClone(defaultLayout);
  newLayout.panels = { ...defaultLayout.panels, ...currentLayout.panels };

  const contentComponentsInLayout = getContentComponentsInLayout(currentLayout);
  const homeGroupByContentComponent = getHomeGroupByContentComponent(defaultLayout);
  const defaultViewsByGroupId = getViewsByGroupId(defaultLayout);

  const panelsByHomeGroup = new Map<string, Set<string>>();
  const unassignedPanelIds = new Set<string>();

  const assignToHomeGroup = (groupId: string, panelId: string) => {
    const groupPanels = panelsByHomeGroup.get(groupId) ?? new Set<string>();
    groupPanels.add(panelId);
    panelsByHomeGroup.set(groupId, groupPanels);
  };

  for (const [panelId, panel] of Object.entries(currentLayout.panels)) {
    const homeGroupId =
      panel.contentComponent != null ? homeGroupByContentComponent.get(panel.contentComponent) : undefined;
    if (homeGroupId != null) {
      assignToHomeGroup(homeGroupId, panelId);
    }
  }

  for (const viewId of getAllViewIdsInLayout(currentLayout)) {
    const panel = currentLayout.panels[viewId];
    if (panel == null || panel.contentComponent == null || homeGroupByContentComponent.has(panel.contentComponent)) {
      continue;
    }
    unassignedPanelIds.add(viewId);
  }

  visitLayoutLeaves(newLayout, (groupId, groupData) => {
    const defaultViews = defaultViewsByGroupId.get(groupId) ?? [];
    const homePanelIds = panelsByHomeGroup.get(groupId) ?? new Set<string>();

    const defaultViewsToAdd = defaultViews.filter(viewId => {
      const panel = defaultLayout.panels[viewId];
      if (panel == null) {
        return false;
      }
      if (currentLayout.panels[viewId] != null) {
        return false;
      }
      return panel.contentComponent != null && !contentComponentsInLayout.has(panel.contentComponent);
    });

    const views: string[] = [];
    for (const viewId of defaultViews) {
      if (homePanelIds.has(viewId) || defaultViewsToAdd.includes(viewId)) {
        views.push(viewId);
      }
    }
    for (const panelId of homePanelIds) {
      if (!views.includes(panelId)) {
        views.push(panelId);
      }
    }
    for (const viewId of defaultViewsToAdd) {
      if (!views.includes(viewId)) {
        views.push(viewId);
      }
    }

    groupData.views = views;
    if (groupData.activeView != null && !views.includes(groupData.activeView)) {
      groupData.activeView = views[0];
    }
  });

  if (unassignedPanelIds.size > 0) {
    const unassignedTargetGroup = getLayoutGroupById(newLayout, MAIN_GROUP_ID) ?? getLargestLayoutGroup(newLayout);
    const unassignedGroupId = unassignedTargetGroup?.data.id;

    if (unassignedGroupId != null) {
      visitLayoutLeaves(newLayout, (groupId, groupData) => {
        if (groupId === unassignedGroupId) {
          groupData.views = uniq([...(groupData.views ?? []), ...unassignedPanelIds]);
        }
      });
    }
  }

  return newLayout;
}
